import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Wrench, MapPin, Star, Search, Filter, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const WorkerDirectory = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [categories, setCategories] = useState([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({
    min_rate: '',
    max_rate: '',
    min_rating: '',
    min_experience: '',
    verified_only: false,
    sort_by: 'rating'
  });

  useEffect(() => {
    fetchCategories();
    fetchWorkers();
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(['all', ...res.data]);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchWorkers = async () => {
    setLoading(true);
    try {
      // Use advanced search if any advanced filters are set
      const hasAdvancedFilters = filters.min_rate || filters.max_rate || filters.min_rating || filters.min_experience || filters.verified_only;
      
      if (hasAdvancedFilters || selectedCategory !== 'all') {
        const searchFilters = {
          category: selectedCategory !== 'all' ? selectedCategory : null,
          min_rate: filters.min_rate ? parseFloat(filters.min_rate) : null,
          max_rate: filters.max_rate ? parseFloat(filters.max_rate) : null,
          min_rating: filters.min_rating ? parseFloat(filters.min_rating) : null,
          min_experience: filters.min_experience ? parseInt(filters.min_experience) : null,
          verified_only: filters.verified_only,
          sort_by: filters.sort_by
        };
        const res = await axios.post(`${API}/workers/advanced-search`, searchFilters);
        setWorkers(res.data);
      } else {
        const res = await axios.get(`${API}/workers`);
        setWorkers(res.data);
      }
    } catch (error) {
      toast.error('Failed to load workers');
    } finally {
      setLoading(false);
    }
  };

  const filteredWorkers = workers.filter(worker =>
    searchQuery === '' ||
    worker.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    worker.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
    worker.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      {/* Header */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button
              data-testid="back-to-home-btn"
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-[#003B5C] hover:text-[#002A42] transition-colors duration-300"
            >
              <ArrowLeft className="w-5 h-5" />
              <Wrench className="w-8 h-8" />
              <span className="text-2xl font-bold font-['Outfit']">SkillYaad</span>
            </button>

            {user && (
              <Button
                data-testid="dashboard-header-btn"
                onClick={() => navigate(user.role === 'worker' ? '/dashboard/worker' : '/dashboard/customer')}
                className="bg-[#003B5C] hover:bg-[#002A42] text-white rounded-full px-6 py-2 font-semibold transition-all duration-300"
              >
                Dashboard
              </Button>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#003B5C] mb-2">Find Skilled Workers</h1>
          <p className="text-[#475569]">Browse and connect with verified professionals in Jamaica</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                data-testid="search-workers-input"
                type="text"
                placeholder="Search by name, category, or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 rounded-lg border-gray-200 focus:border-[#003B5C] focus:ring-2 focus:ring-[#003B5C]/20"
              />
            </div>

            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger data-testid="category-filter-select" className="h-12 rounded-lg border-gray-200">
                <Filter className="w-5 h-5 mr-2 text-gray-400" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat === 'all' ? 'All Categories' : cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Worker Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 border-4 border-[#003B5C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[#475569]">Loading workers...</p>
          </div>
        ) : filteredWorkers.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl">
            <p className="text-[#475569] text-lg">No workers found matching your criteria</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="workers-grid">
            {filteredWorkers.map((worker) => (
              <div
                key={worker.id}
                data-testid={`worker-card-${worker.id}`}
                onClick={() => navigate(`/workers/${worker.id}`)}
                className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer group overflow-hidden"
              >
                {/* Profile Image */}
                <div className="h-48 bg-gradient-to-br from-[#003B5C] to-[#009B4D] relative overflow-hidden">
                  {worker.profile_image ? (
                    <img src={worker.profile_image} alt={worker.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <Wrench className="w-16 h-16 text-white/30" />
                    </div>
                  )}
                  <div className="absolute top-4 right-4">
                    <span className="bg-[#FFB81C] text-[#003B5C] px-3 py-1 rounded-full text-sm font-semibold">
                      {worker.category}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#0F172A] mb-2 group-hover:text-[#003B5C] transition-colors duration-300">
                    {worker.name}
                  </h3>
                  
                  <div className="flex items-center space-x-1 mb-3">
                    <Star className="w-5 h-5 text-[#FFB81C] fill-[#FFB81C]" />
                    <span className="font-semibold text-[#0F172A]">{worker.rating.toFixed(1)}</span>
                    <span className="text-[#94A3B8] text-sm">({worker.review_count} reviews)</span>
                  </div>

                  <div className="flex items-center text-[#475569] mb-4">
                    <MapPin className="w-4 h-4 mr-1" />
                    <span className="text-sm">{worker.location}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-2xl font-bold text-[#003B5C] font-['Space_Grotesk']">
                        ${worker.hourly_rate}
                      </span>
                      <span className="text-[#94A3B8] text-sm">/hour</span>
                    </div>
                    {worker.verified && (
                      <span className="text-[#009B4D] text-xs font-semibold bg-[#009B4D]/10 px-2 py-1 rounded">✓ Verified</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkerDirectory;